import { useState } from 'react';
import { message, Spin, DatePicker, Select, Alert, notification } from 'antd';
import guestStyles from './Guest.styles';
import useSendDataHook from './hooks/SendData.hook';
import ResponseTable from './ResponseTable';
import DynamicFormTable from './DynamicFormTable';
import CustomButton from '../../../../common/CustomButton/CustomButton';
import ErrorDisplay from '../ErrorDisplay';
import useFormsHook from '../../../Forms/Forms.hook.js';
import useGuestValidationHooK from './GuestValidation.hook';

export default function Guest({ hotelId, translate, selectedPms }) {
  const [isChecked, setIsChecked] = useState(false);
  const [bookingIdentifier, setBookingIdentifier] = useState('');
  const [entranceDate, setEntranceDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isDomestic, setIsDomestic] = useState(true);
  const [guestType, setGuestType] = useState('holder');
  const { sendData, loading } = useSendDataHook();
  const { state } = useFormsHook();
  const { mock, setMock } = useGuestValidationHooK({
    hotelId,
    selectedPms,
    formState: state,
    isDomestic,
    guestType,
  });

  const sendDataGuest = async () => {
    if (!bookingIdentifier || !entranceDate) {
      notification.warning({
        message: translate('selector.tabs.warning'),
        description: translate('selector.tabs.warningGuestDescription'),
      });
      return;
    }

    try {
      const response = await sendData({
        hotelId,
        bookingIdentifier,
        mockGuests: mock,
        entranceDate,
        departureDate,
      });
      if (!response.success) message.error('Hubo un problema con la operación');
      else message.success('Operación completada con éxito!');
      setData(response);
    } catch (error) {
      message.error('Hubo un problema con la operación');
      setError(error);
    }
  };

  return (
    <div style={guestStyles.stepDetails}>
      <div style={guestStyles.container}>
        <strong>{translate('selector.tabs.validationSendData')}</strong>
        <p>{translate('validation.guest.description')}</p>

        <div style={guestStyles.content}>
          <div style={guestStyles.leftSection}>
            <label htmlFor="bookingIdentifier" style={guestStyles.subtitle}>
              {translate('validation.guest.bookingIdentifier')}
              <span style={{ color: 'red', marginLeft: 4 }}>*</span>
            </label>
            <input
              type="text"
              id="bookingIdentifier"
              style={guestStyles.inputBox}
              onChange={e => setBookingIdentifier(e.target.value)}
              required
            />

            <label htmlFor="entranceDate" style={guestStyles.subtitle}>
              {translate('checkinConfig.entranceDate')}
              <span style={{ color: 'red', marginLeft: 4 }}>*</span>
            </label>
            <div style={guestStyles.datePicker}>
              <DatePicker
                style={{ width: '100%' }}
                bordered={false}
                placeholder={translate('checkinConfig.entranceDate')}
                onChange={(e, stringValue) => setEntranceDate(stringValue)}
                required
              />
            </div>

            {selectedPms.name === 'Mews' && (
              <>
                <label htmlFor="departureDate" style={guestStyles.subtitle}>
                  {translate('checkinConfig.departureDate')}
                </label>
                <div style={guestStyles.datePicker}>
                  <DatePicker
                    style={{ width: '100%' }}
                    bordered={false}
                    placeholder={translate('checkinConfig.departureDate')}
                    onChange={(e, stringValue) => setDepartureDate(stringValue)}
                  />
                </div>
              </>
            )}

            <div style={guestStyles.toggleContainer}>
              <label style={guestStyles.switchText}>
                {translate('validation.guest.activateCustomMock')}
              </label>
              <label style={guestStyles.switch}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  style={guestStyles.switchInput}
                />
                <span
                  style={{
                    ...guestStyles.slider,
                    ...(isChecked ? guestStyles.inputCheckedSlider : {}),
                  }}
                >
                  <span
                    style={{
                      ...guestStyles.sliderBefore,
                      ...(isChecked ? guestStyles.inputCheckedSliderBefore : {}),
                    }}
                  ></span>
                </span>
              </label>
            </div>

            <div style={guestStyles.buttonContainer}>
              <CustomButton
                style={guestStyles.button}
                text={translate('validation.guest.button')}
                onClick={sendDataGuest}
              />
            </div>
          </div>

          <div style={guestStyles.rightSection}>
            {isChecked && (
              <div>
                {state.isDynamicForm && (
                  <div style={guestStyles.guestSelector}>
                    <div style={guestStyles.selectorGroup}>
                      <label style={guestStyles.subtitle}>
                        {translate('validation.guest.guestType.type')}
                      </label>
                      <Select
                        style={{ width: 130 }}
                        value={guestType}
                        onChange={value => setGuestType(value)}
                      >
                        <option value="holder">
                          {translate('validation.guest.guestType.holder')}
                        </option>
                        <option value="adult">
                          {translate('validation.guest.guestType.adult')}
                        </option>
                        <option value="child">
                          {translate('validation.guest.guestType.child')}
                        </option>
                      </Select>
                    </div>
                    <div style={guestStyles.selectorGroup}>
                      <label style={guestStyles.subtitle}>
                        {translate('validation.guest.guestType.guest')}
                      </label>
                      <Select
                        style={{ width: 130 }}
                        value={isDomestic ? '1' : '0'}
                        onChange={value => setIsDomestic(value === '1')}
                      >
                        <option value="1">
                          {translate('validation.guest.guestType.domestic')}
                        </option>
                        <option value="0">
                          {translate('validation.guest.guestType.nonDomestic')}
                        </option>
                      </Select>
                    </div>
                  </div>
                )}

                {guestType === 'child' && (
                  <Alert
                    style={{ marginTop: 8, textAlign: 'center' }}
                    type="warning"
                    message={translate('validation.guest.warningChildDescription')}
                  />
                )}

                <div style={guestStyles.dynamicFormContainer}>
                  <DynamicFormTable
                    mock={mock}
                    setMock={setMock}
                    translate={translate}
                    isDynamicForm={state.isDynamicForm}
                    isHybridForm={state.selectedformVersion === 'hybrid'}
                  />
                </div>
              </div>
            )}

            {data?.success && data?.mockGuests?.[0]?.fields?.length > 0 && (
              <div style={guestStyles.responseTableContainer}>
                <ResponseTable
                  data={data}
                  translate={translate}
                  isDynamicForm={state.isDynamicForm}
                  isHybridForm={state.selectedformVersion === 'hybrid'}
                />
              </div>
            )}
            {data?.error?.length > 0 && <ErrorDisplay errors={data.error} />}
          </div>
        </div>
      </div>

      {loading && (
        <div style={guestStyles.spinContainer}>
          <Spin size="large" tip="Cargando..." />
        </div>
      )}
    </div>
  );
}
